/**
 * Hooks d'acceptation légale — gating RGPD au boot (cf. KB SAAS-A-131).
 *
 * @remarks
 * `useLegalPending` n'interroge le back que si l'utilisateur est authentifié
 * (le endpoint exige un JWT). `useAcceptLegal` enregistre une acceptation puis
 * invalide la liste des documents en attente pour refléter l'état à jour.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/app/stores/use-auth-store';
import { acceptLegal, fetchPendingLegal, type PendingLegalDocument } from '../api/legal';

const PENDING_LEGAL_KEY = ['legal', 'pending'] as const;

export function useLegalPending() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery<PendingLegalDocument[]>({
    queryKey: PENDING_LEGAL_KEY,
    queryFn: fetchPendingLegal,
    enabled: isAuthenticated,
    // Conformité : pas de mise en cache longue, on revérifie à chaque session.
    staleTime: 0,
    retry: false,
  });
}

export function useAcceptLegal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acceptLegal,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PENDING_LEGAL_KEY });
    },
  });
}
